export const algebra2Lessons = {
  title: "Algebra 2 & Trigonometry",
  description: "Master advanced algebra concepts with detailed explanations and Mr. Augustine's proven shortcuts",
  topics: [
    {
      id: "quadratic-functions",
      title: "Quadratic Functions",
      icon: "ri-function-line",
      lessons: [
        {
          id: "quadratic-basics",
          title: "Quadratic Functions & Graphing",
          duration: "45 min",
          difficulty: "Medium",
          content: {
            introduction: "A quadratic function is a polynomial function of degree 2, written in the form f(x) = ax² + bx + c where a ≠ 0. The graph of a quadratic function is a parabola, which can open upward (a > 0) or downward (a < 0).",
            
            textbookKnowledge: [
              {
                title: "Standard Form",
                content: "f(x) = ax² + bx + c\n• a determines the direction and width of the parabola\n• If a > 0, parabola opens upward (minimum point)\n• If a < 0, parabola opens downward (maximum point)\n• The y-intercept is c (the point where x = 0)"
              },
              {
                title: "Vertex Form",
                content: "f(x) = a(x - h)² + k\n• The vertex is at point (h, k)\n• h represents horizontal shift from origin\n• k represents vertical shift from origin\n• This form makes it easy to identify the vertex directly"
              },
              {
                title: "Finding the Vertex from Standard Form",
                content: "For f(x) = ax² + bx + c:\n• x-coordinate of vertex: x = -b/(2a)\n• y-coordinate of vertex: substitute x back into the function\n• Axis of symmetry: x = -b/(2a)"
              },
              {
                title: "Key Features of Parabolas",
                content: "• Vertex: the turning point (maximum or minimum)\n• Axis of symmetry: vertical line through the vertex\n• y-intercept: where the graph crosses the y-axis (0, c)\n• x-intercepts (roots/zeros): where the graph crosses the x-axis\n• Domain: all real numbers (-∞, ∞)\n• Range: depends on whether parabola opens up or down"
              }
            ],
            
            shortMethods: [
              {
                title: "Mr. Augustine's Vertex Finder",
                content: "To find the vertex quickly:\n1. Calculate x = -b/(2a) — this is your x-coordinate\n2. Plug this x back into the original equation to get y\n3. Vertex is (-b/(2a), y)\n\n**Memory Trick**: 'Negative B over 2A gives you the X-ray' (x-coordinate of vertex)"
              },
              {
                title: "Quick Direction Check",
                content: "Look at the coefficient 'a':\n• a is POSITIVE → parabola SMILES (opens up) ☺\n• a is NEGATIVE → parabola FROWNS (opens down) ☹\n\n**Memory Trick**: 'Positive = Happy face up, Negative = Sad face down'"
              },
              {
                title: "Converting to Vertex Form Fast",
                content: "To convert f(x) = ax² + bx + c to vertex form:\n1. Find h = -b/(2a)\n2. Find k by plugging h into the original equation\n3. Write as f(x) = a(x - h)² + k\n\nNo completing the square needed!"
              }
            ],
            
            workedExamples: [
              {
                problem: "Find the vertex and determine if the parabola opens up or down: f(x) = 2x² - 8x + 5",
                solution: "**Step 1**: Identify a, b, c\na = 2, b = -8, c = 5\n\n**Step 2**: Check direction\nSince a = 2 > 0, the parabola opens UPWARD (smiles)\n\n**Step 3**: Find x-coordinate of vertex\nx = -b/(2a) = -(-8)/(2·2) = 8/4 = 2\n\n**Step 4**: Find y-coordinate of vertex\nf(2) = 2(2)² - 8(2) + 5\nf(2) = 2(4) - 16 + 5\nf(2) = 8 - 16 + 5 = -3\n\n**Answer**: Vertex is (2, -3) and the parabola opens upward"
              },
              {
                problem: "Convert to vertex form: f(x) = -x² + 6x - 4",
                solution: "**Step 1**: Identify a, b, c\na = -1, b = 6, c = -4\n\n**Step 2**: Find h (x-coordinate of vertex)\nh = -b/(2a) = -6/(2·(-1)) = -6/(-2) = 3\n\n**Step 3**: Find k (y-coordinate of vertex)\nk = f(3) = -(3)² + 6(3) - 4\nk = -9 + 18 - 4 = 5\n\n**Step 4**: Write in vertex form\nf(x) = a(x - h)² + k\nf(x) = -1(x - 3)² + 5\nf(x) = -(x - 3)² + 5\n\n**Answer**: f(x) = -(x - 3)² + 5"
              },
              {
                problem: "A parabola has vertex at (4, -7) and passes through point (2, 5). Find the equation in vertex form.",
                solution: "**Step 1**: Start with vertex form\nf(x) = a(x - h)² + k\nVertex (h, k) = (4, -7)\nf(x) = a(x - 4)² - 7\n\n**Step 2**: Use the point (2, 5) to find 'a'\nSubstitute x = 2, f(x) = 5:\n5 = a(2 - 4)² - 7\n5 = a(-2)² - 7\n5 = 4a - 7\n12 = 4a\na = 3\n\n**Step 3**: Write final equation\nf(x) = 3(x - 4)² - 7\n\n**Answer**: f(x) = 3(x - 4)² - 7"
              }
            ]
          },
          practiceQuestions: [
            {
              question: "Find the vertex of f(x) = x² - 6x + 8",
              answer: "Vertex: (3, -1)",
              solution: "x = -b/(2a) = -(-6)/(2·1) = 6/2 = 3\nf(3) = (3)² - 6(3) + 8 = 9 - 18 + 8 = -1\nVertex: (3, -1)"
            },
            {
              question: "Does f(x) = -3x² + 12x - 5 open upward or downward?",
              answer: "Downward",
              solution: "a = -3 < 0, so the parabola opens downward (frowns)"
            },
            {
              question: "Convert to vertex form: f(x) = x² + 4x + 1",
              answer: "f(x) = (x + 2)² - 3",
              solution: "h = -4/(2·1) = -2\nk = f(-2) = (-2)² + 4(-2) + 1 = 4 - 8 + 1 = -3\nf(x) = (x - (-2))² + (-3) = (x + 2)² - 3"
            },
            {
              question: "Find the axis of symmetry for f(x) = 2x² - 8x + 3",
              answer: "x = 2",
              solution: "Axis of symmetry: x = -b/(2a) = -(-8)/(2·2) = 8/4 = 2"
            },
            {
              question: "What is the y-intercept of f(x) = 3x² - 5x + 7?",
              answer: "(0, 7)",
              solution: "The y-intercept is c = 7, so the point is (0, 7)"
            },
            {
              question: "Find the vertex of f(x) = -2x² + 12x - 10",
              answer: "Vertex: (3, 8)",
              solution: "x = -12/(2·(-2)) = -12/(-4) = 3\nf(3) = -2(3)² + 12(3) - 10 = -18 + 36 - 10 = 8\nVertex: (3, 8)"
            },
            {
              question: "Convert to vertex form: f(x) = 2x² - 12x + 14",
              answer: "f(x) = 2(x - 3)² - 4",
              solution: "h = -(-12)/(2·2) = 12/4 = 3\nk = f(3) = 2(3)² - 12(3) + 14 = 18 - 36 + 14 = -4\nf(x) = 2(x - 3)² - 4"
            },
            {
              question: "A parabola has vertex (5, 2) and a = -1. Write the equation in vertex form.",
              answer: "f(x) = -(x - 5)² + 2",
              solution: "f(x) = a(x - h)² + k = -1(x - 5)² + 2 = -(x - 5)² + 2"
            },
            {
              question: "Find the vertex of f(x) = 4x² + 16x + 11",
              answer: "Vertex: (-2, -5)",
              solution: "x = -16/(2·4) = -16/8 = -2\nf(-2) = 4(-2)² + 16(-2) + 11 = 16 - 32 + 11 = -5\nVertex: (-2, -5)"
            },
            {
              question: "What is the range of f(x) = x² - 4x + 7?",
              answer: "[3, ∞)",
              solution: "Vertex x = -(-4)/(2·1) = 2, f(2) = 4 - 8 + 7 = 3\nSince a > 0 (opens up), minimum is 3\nRange: [3, ∞)"
            },
            {
              question: "Convert to vertex form: f(x) = -x² - 6x - 5",
              answer: "f(x) = -(x + 3)² + 4",
              solution: "h = -(-6)/(2·(-1)) = 6/(-2) = -3\nk = f(-3) = -(-3)² - 6(-3) - 5 = -9 + 18 - 5 = 4\nf(x) = -(x + 3)² + 4"
            },
            {
              question: "Find the axis of symmetry for f(x) = -3x² + 18x - 20",
              answer: "x = 3",
              solution: "x = -18/(2·(-3)) = -18/(-6) = 3"
            },
            {
              question: "A parabola has vertex (-1, 6) and passes through (0, 4). Find 'a'.",
              answer: "a = -2",
              solution: "f(x) = a(x + 1)² + 6\n4 = a(0 + 1)² + 6\n4 = a + 6\na = -2"
            },
            {
              question: "Find the vertex of f(x) = 3x² - 18x + 24",
              answer: "Vertex: (3, -3)",
              solution: "x = -(-18)/(2·3) = 18/6 = 3\nf(3) = 3(3)² - 18(3) + 24 = 27 - 54 + 24 = -3\nVertex: (3, -3)"
            },
            {
              question: "Does f(x) = 5x² - 10x + 1 have a maximum or minimum?",
              answer: "Minimum",
              solution: "a = 5 > 0, so parabola opens upward and has a minimum"
            },
            {
              question: "Convert to vertex form: f(x) = x² - 10x + 21",
              answer: "f(x) = (x - 5)² - 4",
              solution: "h = -(-10)/(2·1) = 5\nk = f(5) = 25 - 50 + 21 = -4\nf(x) = (x - 5)² - 4"
            },
            {
              question: "What is the minimum value of f(x) = 2x² - 8x + 11?",
              answer: "3",
              solution: "Vertex x = -(-8)/(2·2) = 2\nf(2) = 2(4) - 16 + 11 = 3\nMinimum value: 3"
            },
            {
              question: "Find the vertex of f(x) = -x² + 8x - 12",
              answer: "Vertex: (4, 4)",
              solution: "x = -8/(2·(-1)) = 4\nf(4) = -(4)² + 8(4) - 12 = -16 + 32 - 12 = 4\nVertex: (4, 4)"
            },
            {
              question: "A parabola has vertex (2, -5) and opens downward with |a| = 3. Write the equation.",
              answer: "f(x) = -3(x - 2)² - 5",
              solution: "Opens downward means a < 0, so a = -3\nf(x) = -3(x - 2)² - 5"
            },
            {
              question: "Convert to vertex form: f(x) = 2x² + 8x + 3",
              answer: "f(x) = 2(x + 2)² - 5",
              solution: "h = -8/(2·2) = -2\nk = f(-2) = 2(4) + 8(-2) + 3 = 8 - 16 + 3 = -5\nf(x) = 2(x + 2)² - 5"
            }
          ]
        },
        {
          id: "solving-quadratics",
          title: "Solving Quadratic Equations",
          duration: "50 min",
          difficulty: "Medium",
          content: {
            introduction: "Quadratic equations can be solved using multiple methods: factoring, completing the square, the quadratic formula, and graphing. Each method has its advantages depending on the form of the equation.",
            
            textbookKnowledge: [
              {
                title: "The Quadratic Formula",
                content: "For ax² + bx + c = 0:\n\nx = (-b ± √(b² - 4ac)) / (2a)\n\nThis formula works for ALL quadratic equations and gives both solutions (if they exist)."
              },
              {
                title: "The Discriminant",
                content: "The discriminant is Δ = b² - 4ac\n\n• If Δ > 0: Two distinct real solutions\n• If Δ = 0: One repeated real solution (double root)\n• If Δ < 0: No real solutions (two complex solutions)\n\nThe discriminant tells you how many solutions exist WITHOUT solving!"
              },
              {
                title: "Factoring Method",
                content: "If ax² + bx + c can be factored as (mx + p)(nx + q) = 0:\n• Set each factor equal to zero\n• Solve mx + p = 0 and nx + q = 0\n• This method is fastest when the equation factors nicely"
              },
              {
                title: "Completing the Square",
                content: "To solve x² + bx + c = 0:\n1. Move c to the right side: x² + bx = -c\n2. Add (b/2)² to both sides\n3. Factor the left side as a perfect square\n4. Take the square root of both sides\n5. Solve for x"
              },
              {
                title: "Zero Product Property",
                content: "If A · B = 0, then A = 0 or B = 0\n\nThis is the foundation of the factoring method. When you factor a quadratic into (x - r₁)(x - r₂) = 0, you can set each factor to zero to find the solutions."
              }
            ],
            
            shortMethods: [
              {
                title: "Mr. Augustine's Formula Memory Trick",
                content: "Quadratic Formula: x = (-b ± √(b² - 4ac)) / (2a)\n\n**Song Method**: Sing to 'Pop Goes the Weasel'\n'X equals negative B,\nPlus or minus square root,\nB squared minus 4AC,\nAll over 2A!'\n\n**Visual**: Draw a fraction bar, put -b ± √ on top, 2a on bottom"
              },
              {
                title: "Quick Discriminant Check",
                content: "Before solving, check b² - 4ac:\n• POSITIVE → 2 solutions (keep going)\n• ZERO → 1 solution (easier!)\n• NEGATIVE → No real solutions (stop, or use complex numbers)\n\nSaves time by telling you what to expect!"
              },
              {
                title: "Fast Factoring Test",
                content: "For x² + bx + c:\n• Find two numbers that MULTIPLY to c and ADD to b\n• If you can find them in 10 seconds → factor\n• If not → use the quadratic formula\n\nDon't waste time trying to factor if it doesn't factor nicely!"
              },
              {
                title: "Sum and Product of Roots Shortcut",
                content: "For ax² + bx + c = 0 with roots r₁ and r₂:\n• Sum of roots: r₁ + r₂ = -b/a\n• Product of roots: r₁ · r₂ = c/a\n\nUse this to check your answers or find one root when you know the other!"
              }
            ],
            
            workedExamples: [
              {
                problem: "Solve using the quadratic formula: 2x² - 7x + 3 = 0",
                solution: "**Step 1**: Identify a, b, c\na = 2, b = -7, c = 3\n\n**Step 2**: Check discriminant\nΔ = b² - 4ac = (-7)² - 4(2)(3) = 49 - 24 = 25 > 0\nTwo real solutions exist ✓\n\n**Step 3**: Apply quadratic formula\nx = (-b ± √Δ) / (2a)\nx = (-(-7) ± √25) / (2·2)\nx = (7 ± 5) / 4\n\n**Step 4**: Find both solutions\nx = (7 + 5)/4 = 12/4 = 3\nx = (7 - 5)/4 = 2/4 = 1/2\n\n**Answer**: x = 3 or x = 1/2"
              },
              {
                problem: "Solve by factoring: x² - 5x - 14 = 0",
                solution: "**Step 1**: Find two numbers that multiply to -14 and add to -5\nNumbers: -7 and +2\n(-7) × (+2) = -14 ✓\n(-7) + (+2) = -5 ✓\n\n**Step 2**: Factor\nx² - 5x - 14 = (x - 7)(x + 2) = 0\n\n**Step 3**: Apply zero product property\nx - 7 = 0  →  x = 7\nx + 2 = 0  →  x = -2\n\n**Answer**: x = 7 or x = -2"
              },
              {
                problem: "Determine the number of real solutions: 3x² - 2x + 5 = 0",
                solution: "**Step 1**: Identify a, b, c\na = 3, b = -2, c = 5\n\n**Step 2**: Calculate discriminant\nΔ = b² - 4ac\nΔ = (-2)² - 4(3)(5)\nΔ = 4 - 60\nΔ = -56\n\n**Step 3**: Interpret\nSince Δ < 0, there are NO real solutions\n(The equation has two complex solutions)\n\n**Answer**: 0 real solutions"
              }
            ]
          },
          practiceQuestions: [
            {
              question: "Solve using the quadratic formula: x² + 5x + 6 = 0",
              answer: "x = -2 or x = -3",
              solution: "a=1, b=5, c=6\nΔ = 25 - 24 = 1\nx = (-5 ± 1)/2 = -2 or -3"
            },
            {
              question: "Find the discriminant of 2x² - 3x + 4 = 0",
              answer: "Δ = -23",
              solution: "Δ = b² - 4ac = (-3)² - 4(2)(4) = 9 - 32 = -23"
            },
            {
              question: "Solve by factoring: x² - 9 = 0",
              answer: "x = 3 or x = -3",
              solution: "(x - 3)(x + 3) = 0\nx = 3 or x = -3"
            },
            {
              question: "How many real solutions does x² - 6x + 9 = 0 have?",
              answer: "1 solution",
              solution: "Δ = 36 - 36 = 0, so one repeated solution"
            },
            {
              question: "Solve: x² + 7x + 12 = 0",
              answer: "x = -3 or x = -4",
              solution: "(x + 3)(x + 4) = 0\nx = -3 or x = -4"
            },
            {
              question: "Solve using the quadratic formula: x² - 4x + 1 = 0",
              answer: "x = 2 ± √3",
              solution: "x = (4 ± √(16-4))/2 = (4 ± √12)/2 = (4 ± 2√3)/2 = 2 ± √3"
            },
            {
              question: "Find the discriminant: 4x² + 12x + 9 = 0",
              answer: "Δ = 0",
              solution: "Δ = 144 - 144 = 0"
            },
            {
              question: "Solve by factoring: x² - x - 20 = 0",
              answer: "x = 5 or x = -4",
              solution: "(x - 5)(x + 4) = 0\nx = 5 or x = -4"
            },
            {
              question: "Solve: 2x² - 8x = 0",
              answer: "x = 0 or x = 4",
              solution: "2x(x - 4) = 0\nx = 0 or x = 4"
            },
            {
              question: "How many real solutions: x² + 2x + 5 = 0?",
              answer: "0 solutions",
              solution: "Δ = 4 - 20 = -16 < 0, no real solutions"
            },
            {
              question: "Solve using the quadratic formula: 3x² + 2x - 1 = 0",
              answer: "x = 1/3 or x = -1",
              solution: "x = (-2 ± √(4+12))/6 = (-2 ± 4)/6\nx = 2/6 = 1/3 or x = -6/6 = -1"
            },
            {
              question: "Solve by factoring: x² + 10x + 25 = 0",
              answer: "x = -5",
              solution: "(x + 5)² = 0\nx = -5 (double root)"
            },
            {
              question: "Find the sum of the roots of 2x² - 6x + 3 = 0",
              answer: "3",
              solution: "Sum = -b/a = -(-6)/2 = 3"
            },
            {
              question: "Solve: x² = 16",
              answer: "x = 4 or x = -4",
              solution: "x² - 16 = 0\n(x - 4)(x + 4) = 0\nx = ±4"
            },
            {
              question: "Find the product of the roots of x² - 7x + 10 = 0",
              answer: "10",
              solution: "Product = c/a = 10/1 = 10"
            },
            {
              question: "Solve using the quadratic formula: x² + 3x - 2 = 0",
              answer: "x = (-3 ± √17)/2",
              solution: "x = (-3 ± √(9+8))/2 = (-3 ± √17)/2"
            },
            {
              question: "Solve by factoring: 2x² + 7x + 3 = 0",
              answer: "x = -1/2 or x = -3",
              solution: "(2x + 1)(x + 3) = 0\nx = -1/2 or x = -3"
            },
            {
              question: "How many real solutions: 5x² - 3x + 1 = 0?",
              answer: "0 solutions",
              solution: "Δ = 9 - 20 = -11 < 0"
            },
            {
              question: "Solve: x² - 12x + 36 = 0",
              answer: "x = 6",
              solution: "(x - 6)² = 0\nx = 6"
            },
            {
              question: "Solve using the quadratic formula: 2x² + 5x - 3 = 0",
              answer: "x = 1/2 or x = -3",
              solution: "x = (-5 ± √(25+24))/4 = (-5 ± 7)/4\nx = 2/4 = 1/2 or x = -12/4 = -3"
            }
          ]
        }
      ]
    },
    {
      id: "exponential-equations",
      title: "Exponential Equations",
      icon: "ri-line-chart-line",
      lessons: [
        {
          id: "exponential-basics",
          title: "Exponential Functions & Growth",
          duration: "45 min",
          difficulty: "Medium",
          content: {
            introduction: "An exponential function has the form f(x) = a·bˣ where b > 0 and b ≠ 1. These functions model rapid growth or decay and appear in real-world scenarios like population growth, compound interest, and radioactive decay.",
            
            textbookKnowledge: [
              {
                title: "Exponential Function Form",
                content: "f(x) = a·bˣ\n• a = initial value (y-intercept when x = 0)\n• b = base (growth/decay factor)\n• x = exponent (independent variable)\n• If b > 1: exponential GROWTH\n• If 0 < b < 1: exponential DECAY"
              },
              {
                title: "Properties of Exponential Functions",
                content: "• Domain: all real numbers (-∞, ∞)\n• Range: (0, ∞) if a > 0, or (-∞, 0) if a < 0\n• Horizontal asymptote: y = 0\n• Always passes through (0, a)\n• Never touches or crosses the x-axis\n• Always increasing (if b > 1) or decreasing (if 0 < b < 1)"
              },
              {
                title: "Exponential Growth vs Decay",
                content: "**Growth** (b > 1):\n• Population growth\n• Compound interest\n• Viral spread\n• Formula: A = A₀(1 + r)ᵗ where r > 0\n\n**Decay** (0 < b < 1):\n• Radioactive decay\n• Depreciation\n• Cooling\n• Formula: A = A₀(1 - r)ᵗ where 0 < r < 1"
              },
              {
                title: "Laws of Exponents",
                content: "• bᵐ · bⁿ = bᵐ⁺ⁿ\n• bᵐ / bⁿ = bᵐ⁻ⁿ\n• (bᵐ)ⁿ = bᵐⁿ\n• b⁰ = 1 (b ≠ 0)\n• b⁻ⁿ = 1/bⁿ\n• (ab)ⁿ = aⁿbⁿ\n• (a/b)ⁿ = aⁿ/bⁿ"
              },
              {
                title: "The Number e",
                content: "e ≈ 2.71828... is a special irrational number\n• Natural exponential function: f(x) = eˣ\n• Appears in continuous growth/decay\n• Formula: A = A₀eᵏᵗ\n• k > 0: continuous growth\n• k < 0: continuous decay"
              }
            ],
            
            shortMethods: [
              {
                title: "Mr. Augustine's Growth/Decay Detector",
                content: "Look at the base 'b':\n• b > 1 → GROWTH (getting bigger) 📈\n• 0 < b < 1 → DECAY (getting smaller) 📉\n\n**Quick Check**: If b = 1.05, that's 1 + 0.05 = 5% growth\nIf b = 0.95, that's 1 - 0.05 = 5% decay\n\n**Memory Trick**: 'Base bigger than 1 = Building up, Base less than 1 = Breaking down'"
              },
              {
                title: "Fast Initial Value Finder",
                content: "To find the y-intercept (initial value):\nJust plug in x = 0!\n\nf(0) = a·b⁰ = a·1 = a\n\nThe initial value is always 'a' — no calculation needed!"
              },
              {
                title: "Percent Change to Base Conversion",
                content: "If something grows by r% per period:\n• Base b = 1 + (r/100)\n• Example: 8% growth → b = 1.08\n\nIf something decays by r% per period:\n• Base b = 1 - (r/100)\n• Example: 15% decay → b = 0.85\n\n**Shortcut**: Just move the decimal and add/subtract from 1!"
              }
            ],
            
            workedExamples: [
              {
                problem: "A population of bacteria starts at 500 and doubles every 3 hours. Write an exponential function and find the population after 12 hours.",
                solution: "**Step 1**: Identify the initial value and growth factor\nInitial value: a = 500\nDoubles means multiplies by 2, so b = 2\nTime period: every 3 hours\n\n**Step 2**: Write the function\nSince it doubles every 3 hours, we need x to represent 3-hour periods\nf(x) = 500·2ˣ where x = number of 3-hour periods\n\n**Step 3**: Find population after 12 hours\n12 hours = 12/3 = 4 periods\nf(4) = 500·2⁴ = 500·16 = 8,000\n\n**Answer**: f(x) = 500·2ˣ; Population after 12 hours is 8,000"
              },
              {
                problem: "A car worth $25,000 depreciates by 12% each year. Write the exponential decay function and find its value after 5 years.",
                solution: "**Step 1**: Identify initial value and decay rate\nInitial value: a = 25,000\nDecay rate: r = 12% = 0.12\n\n**Step 2**: Find the base\nDecay means b = 1 - r = 1 - 0.12 = 0.88\n\n**Step 3**: Write the function\nf(x) = 25,000·(0.88)ˣ where x = years\n\n**Step 4**: Find value after 5 years\nf(5) = 25,000·(0.88)⁵\nf(5) = 25,000·0.5277\nf(5) ≈ 13,193\n\n**Answer**: f(x) = 25,000(0.88)ˣ; Value after 5 years is approximately $13,193"
              },
              {
                problem: "Simplify: (2³)² · 2⁴ / 2⁵",
                solution: "**Step 1**: Apply power of a power rule\n(2³)² = 2³ˣ² = 2⁶\n\n**Step 2**: Rewrite the expression\n2⁶ · 2⁴ / 2⁵\n\n**Step 3**: Apply product rule in numerator\n2⁶ · 2⁴ = 2⁶⁺⁴ = 2¹⁰\n\n**Step 4**: Apply quotient rule\n2¹⁰ / 2⁵ = 2¹⁰⁻⁵ = 2⁵\n\n**Step 5**: Evaluate\n2⁵ = 32\n\n**Answer**: 32"
              }
            ]
          },
          practiceQuestions: [
            {
              question: "Is f(x) = 3·(1.5)ˣ growth or decay?",
              answer: "Growth",
              solution: "Base b = 1.5 > 1, so it's exponential growth"
            },
            {
              question: "What is the initial value of f(x) = 8·(0.7)ˣ?",
              answer: "8",
              solution: "f(0) = 8·(0.7)⁰ = 8·1 = 8"
            },
            {
              question: "Simplify: 5³ · 5⁴",
              answer: "5⁷",
              solution: "5³ · 5⁴ = 5³⁺⁴ = 5⁷"
            },
            {
              question: "A population grows by 6% per year. What is the base b?",
              answer: "b = 1.06",
              solution: "Growth: b = 1 + r = 1 + 0.06 = 1.06"
            },
            {
              question: "Evaluate: 2⁻³",
              answer: "1/8",
              solution: "2⁻³ = 1/2³ = 1/8"
            },
            {
              question: "Is f(x) = 10·(0.85)ˣ growth or decay?",
              answer: "Decay",
              solution: "Base b = 0.85 < 1, so it's exponential decay"
            },
            {
              question: "Simplify: (3²)³",
              answer: "3⁶ or 729",
              solution: "(3²)³ = 3²ˣ³ = 3⁶ = 729"
            },
            {
              question: "If $1000 grows at 5% annually, write the function.",
              answer: "f(x) = 1000(1.05)ˣ",
              solution: "a = 1000, b = 1 + 0.05 = 1.05\nf(x) = 1000(1.05)ˣ"
            },
            {
              question: "Simplify: 7⁸ / 7⁵",
              answer: "7³",
              solution: "7⁸ / 7⁵ = 7⁸⁻⁵ = 7³"
            },
            {
              question: "What is the horizontal asymptote of f(x) = 4·(2)ˣ?",
              answer: "y = 0",
              solution: "All exponential functions have horizontal asymptote y = 0"
            },
            {
              question: "A substance decays by 20% per hour. What is the base?",
              answer: "b = 0.8",
              solution: "Decay: b = 1 - r = 1 - 0.20 = 0.8"
            },
            {
              question: "Evaluate: 4⁰",
              answer: "1",
              solution: "Any non-zero number to the power 0 equals 1"
            },
            {
              question: "Simplify: (2 · 3)⁴",
              answer: "2⁴ · 3⁴ or 1296",
              solution: "(2 · 3)⁴ = 2⁴ · 3⁴ = 16 · 81 = 1296"
            },
            {
              question: "If f(x) = 50·(1.08)ˣ, find f(0).",
              answer: "50",
              solution: "f(0) = 50·(1.08)⁰ = 50·1 = 50"
            },
            {
              question: "Simplify: 10³ · 10⁻⁵",
              answer: "10⁻² or 1/100",
              solution: "10³ · 10⁻⁵ = 10³⁺⁽⁻⁵⁾ = 10⁻² = 1/100"
            },
            {
              question: "A population of 2000 triples every 4 years. Write the function.",
              answer: "f(x) = 2000·(3)ˣ",
              solution: "Initial: 2000, triples means b = 3\nf(x) = 2000·(3)ˣ where x = 4-year periods"
            },
            {
              question: "Simplify: (5/2)²",
              answer: "25/4",
              solution: "(5/2)² = 5²/2² = 25/4"
            },
            {
              question: "Is f(x) = 6·(1)ˣ exponential?",
              answer: "No",
              solution: "b = 1, but exponential functions require b ≠ 1. This is constant."
            },
            {
              question: "Evaluate: 3⁻²",
              answer: "1/9",
              solution: "3⁻² = 1/3² = 1/9"
            },
            {
              question: "If $5000 decays at 8% per year, find the value after 1 year.",
              answer: "$4600",
              solution: "f(x) = 5000(0.92)ˣ\nf(1) = 5000(0.92) = 4600"
            }
          ]
        },
        {
          id: "solving-exponential",
          title: "Solving Exponential Equations",
          duration: "50 min",
          difficulty: "Hard",
          content: {
            introduction: "Solving exponential equations requires using properties of exponents and logarithms. The key strategy is to get the same base on both sides, or use logarithms when that's not possible.",
            
            textbookKnowledge: [
              {
                title: "Same Base Method",
                content: "If bᵐ = bⁿ, then m = n\n\nSteps:\n1. Rewrite both sides with the same base\n2. Set the exponents equal\n3. Solve for the variable\n\nExample: 2ˣ = 8\n2ˣ = 2³\nx = 3"
              },
              {
                title: "When Bases Can't Match",
                content: "If you can't get the same base, use logarithms:\n\nFor bˣ = c:\n1. Take log of both sides: log(bˣ) = log(c)\n2. Use power rule: x·log(b) = log(c)\n3. Solve: x = log(c)/log(b)\n\nThis is called the change of base formula."
              },
              {
                title: "Common Base Conversions",
                content: "• 4 = 2²\n• 8 = 2³\n• 16 = 2⁴\n• 9 = 3²\n• 27 = 3³\n• 25 = 5²\n• 125 = 5³\n• 1/2 = 2⁻¹\n• 1/4 = 2⁻²\n\nMemorize these to quickly rewrite equations!"
              },
              {
                title: "Exponential Equation Types",
                content: "**Type 1**: bˣ = c (single exponential)\n**Type 2**: a·bˣ = c (with coefficient)\n**Type 3**: bᵐˣ⁺ⁿ = c (linear exponent)\n**Type 4**: bˣ + bˣ = c (sum of exponentials)\n**Type 5**: bˣ · bʸ = c (product of exponentials)"
              }
            ],
            
            shortMethods: [
              {
                title: "Mr. Augustine's Base Matching Strategy",
                content: "**Step 1**: Look at both sides — can you write them with the same base?\n• If YES → match bases and set exponents equal\n• If NO → use logarithms\n\n**Quick Test**: Ask yourself:\n'Is the right side a power of the left side's base?'\n\nExample: 3ˣ = 81\n81 = 3⁴? YES! → 3ˣ = 3⁴ → x = 4"
              },
              {
                title: "The Isolation Trick",
                content: "If there's a coefficient, isolate the exponential first:\n\n5·2ˣ = 40\n↓ (divide by 5)\n2ˣ = 8\n↓ (now match bases)\n2ˣ = 2³\nx = 3\n\n**Rule**: Always isolate bˣ before matching bases!"
              },
              {
                title: "Negative Exponent Flip",
                content: "If you see a fraction, flip it to get a negative exponent:\n\n2ˣ = 1/8\n↓\n2ˣ = 2⁻³\nx = -3\n\n**Memory Trick**: 'Fraction flips to negative'"
              }
            ],
            
            workedExamples: [
              {
                problem: "Solve: 5ˣ⁺² = 125",
                solution: "**Step 1**: Rewrite 125 as a power of 5\n125 = 5³\n\n**Step 2**: Rewrite equation with same base\n5ˣ⁺² = 5³\n\n**Step 3**: Set exponents equal\nx + 2 = 3\n\n**Step 4**: Solve for x\nx = 3 - 2\nx = 1\n\n**Check**: 5¹⁺² = 5³ = 125 ✓\n\n**Answer**: x = 1"
              },
              {
                problem: "Solve: 3·2ˣ = 96",
                solution: "**Step 1**: Isolate the exponential\n2ˣ = 96/3\n2ˣ = 32\n\n**Step 2**: Rewrite 32 as a power of 2\n32 = 2⁵\n\n**Step 3**: Match bases\n2ˣ = 2⁵\n\n**Step 4**: Set exponents equal\nx = 5\n\n**Check**: 3·2⁵ = 3·32 = 96 ✓\n\n**Answer**: x = 5"
              },
              {
                problem: "Solve: 4ˣ = 8",
                solution: "**Step 1**: Rewrite both sides as powers of 2\n4 = 2²  and  8 = 2³\n(2²)ˣ = 2³\n\n**Step 2**: Apply power rule\n2²ˣ = 2³\n\n**Step 3**: Set exponents equal\n2x = 3\n\n**Step 4**: Solve for x\nx = 3/2\n\n**Check**: 4³ᐟ² = (2²)³ᐟ² = 2³ = 8 ✓\n\n**Answer**: x = 3/2"
              }
            ]
          },
          practiceQuestions: [
            {
              question: "Solve: 2ˣ = 16",
              answer: "x = 4",
              solution: "2ˣ = 2⁴\nx = 4"
            },
            {
              question: "Solve: 3ˣ = 27",
              answer: "x = 3",
              solution: "3ˣ = 3³\nx = 3"
            },
            {
              question: "Solve: 5ˣ = 1/25",
              answer: "x = -2",
              solution: "5ˣ = 1/5² = 5⁻²\nx = -2"
            },
            {
              question: "Solve: 2ˣ⁺¹ = 32",
              answer: "x = 4",
              solution: "2ˣ⁺¹ = 2⁵\nx + 1 = 5\nx = 4"
            },
            {
              question: "Solve: 4·3ˣ = 108",
              answer: "x = 3",
              solution: "3ˣ = 27 = 3³\nx = 3"
            },
            {
              question: "Solve: 10ˣ = 1000",
              answer: "x = 3",
              solution: "10ˣ = 10³\nx = 3"
            },
            {
              question: "Solve: 2ˣ⁻³ = 8",
              answer: "x = 6",
              solution: "2ˣ⁻³ = 2³\nx - 3 = 3\nx = 6"
            },
            {
              question: "Solve: 9ˣ = 3",
              answer: "x = 1/2",
              solution: "(3²)ˣ = 3¹\n3²ˣ = 3¹\n2x = 1\nx = 1/2"
            },
            {
              question: "Solve: 5ˣ = 1",
              answer: "x = 0",
              solution: "5ˣ = 5⁰\nx = 0"
            },
            {
              question: "Solve: 2·4ˣ = 32",
              answer: "x = 2",
              solution: "4ˣ = 16 = 4²\nx = 2"
            },
            {
              question: "Solve: 3²ˣ = 81",
              answer: "x = 2",
              solution: "3²ˣ = 3⁴\n2x = 4\nx = 2"
            },
            {
              question: "Solve: 7ˣ = 1/49",
              answer: "x = -2",
              solution: "7ˣ = 1/7² = 7⁻²\nx = -2"
            },
            {
              question: "Solve: 16ˣ = 4",
              answer: "x = 1/2",
              solution: "(2⁴)ˣ = 2²\n2⁴ˣ = 2²\n4x = 2\nx = 1/2"
            },
            {
              question: "Solve: 5ˣ⁺¹ = 25",
              answer: "x = 1",
              solution: "5ˣ⁺¹ = 5²\nx + 1 = 2\nx = 1"
            },
            {
              question: "Solve: 8ˣ = 2",
              answer: "x = 1/3",
              solution: "(2³)ˣ = 2¹\n2³ˣ = 2¹\n3x = 1\nx = 1/3"
            },
            {
              question: "Solve: 3·5ˣ = 375",
              answer: "x = 3",
              solution: "5ˣ = 125 = 5³\nx = 3"
            },
            {
              question: "Solve: 2³ˣ = 64",
              answer: "x = 2",
              solution: "2³ˣ = 2⁶\n3x = 6\nx = 2"
            },
            {
              question: "Solve: 4ˣ = 1/16",
              answer: "x = -2",
              solution: "4ˣ = 1/4² = 4⁻²\nx = -2"
            },
            {
              question: "Solve: 27ˣ = 9",
              answer: "x = 2/3",
              solution: "(3³)ˣ = 3²\n3³ˣ = 3²\n3x = 2\nx = 2/3"
            },
            {
              question: "Solve: 6·2ˣ = 192",
              answer: "x = 5",
              solution: "2ˣ = 32 = 2⁵\nx = 5"
            }
          ]
        }
      ]
    },
    {
      id: "logarithms",
      title: "Logarithms",
      icon: "ri-calculator-line",
      lessons: [
        {
          id: "logarithm-basics",
          title: "Introduction to Logarithms",
          duration: "45 min",
          difficulty: "Hard",
          content: {
            introduction: "A logarithm is the inverse operation of exponentiation. If bˣ = y, then log_b(y) = x. Logarithms answer the question: 'To what power must I raise the base to get this number?'",
            
            textbookKnowledge: [
              {
                title: "Logarithm Definition",
                content: "log_b(y) = x  means  bˣ = y\n\nWhere:\n• b = base (b > 0, b ≠ 1)\n• y = argument (y > 0)\n• x = the logarithm (the exponent)\n\nExample: log₂(8) = 3 because 2³ = 8"
              },
              {
                title: "Special Logarithms",
                content: "**Common Logarithm**: log(x) = log₁₀(x)\n• Base 10 (usually written without the base)\n• Used in science and engineering\n\n**Natural Logarithm**: ln(x) = log_e(x)\n• Base e ≈ 2.71828\n• Used in calculus and continuous growth/decay"
              },
              {
                title: "Basic Logarithm Properties",
                content: "• log_b(1) = 0  (because b⁰ = 1)\n• log_b(b) = 1  (because b¹ = b)\n• log_b(bˣ) = x  (inverse property)\n• b^(log_b(x)) = x  (inverse property)\n• log_b(b^x) = x"
              },
              {
                title: "Laws of Logarithms",
                content: "**Product Rule**: log_b(MN) = log_b(M) + log_b(N)\n\n**Quotient Rule**: log_b(M/N) = log_b(M) - log_b(N)\n\n**Power Rule**: log_b(M^p) = p·log_b(M)\n\n**Change of Base**: log_b(M) = log(M)/log(b) = ln(M)/ln(b)"
              },
              {
                title: "Converting Between Forms",
                content: "**Exponential to Logarithmic**:\nbˣ = y  →  log_b(y) = x\n\n**Logarithmic to Exponential**:\nlog_b(y) = x  →  bˣ = y\n\nExample:\n2⁵ = 32  ↔  log₂(32) = 5"
              }
            ],
            
            shortMethods: [
              {
                title: "Mr. Augustine's Log Translation Method",
                content: "To convert bˣ = y to logarithm form:\n\n**Think**: 'The EXPONENT becomes the ANSWER'\n\nbˣ = y  →  log_b(y) = x\n ↑       ↑         ↑\nbase  result  exponent\n\n**Memory Trick**: 'Base stays base, result goes inside, exponent is the answer'\n\nExample: 3⁴ = 81\nBase 3, result 81, exponent 4\nlog₃(81) = 4"
              },
              {
                title: "Quick Evaluation Trick",
                content: "To evaluate log_b(y), ask:\n'What power of b gives me y?'\n\nlog₂(16) = ?\n'What power of 2 gives 16?'\n2⁴ = 16, so log₂(16) = 4\n\n**Shortcut**: If you know your powers, logs are instant!"
              },
              {
                title: "The Three Magic Logs",
                content: "Memorize these forever:\n\n1. log_b(1) = 0  (any base to power 0 is 1)\n2. log_b(b) = 1  (any base to power 1 is itself)\n3. log_b(b^x) = x  (inverse property)\n\nThese solve 80% of basic log problems instantly!"
              }
            ],
            
            workedExamples: [
              {
                problem: "Evaluate: log₅(125)",
                solution: "**Step 1**: Ask 'What power of 5 gives 125?'\nWe need to find x where 5ˣ = 125\n\n**Step 2**: Recognize 125 as a power of 5\n5¹ = 5\n5² = 25\n5³ = 125 ✓\n\n**Step 3**: Write the answer\nlog₅(125) = 3\n\n**Answer**: 3"
              },
              {
                problem: "Convert to logarithmic form: 2⁶ = 64",
                solution: "**Step 1**: Identify base, exponent, and result\nBase: b = 2\nExponent: x = 6\nResult: y = 64\n\n**Step 2**: Apply the conversion\nbˣ = y  →  log_b(y) = x\n2⁶ = 64  →  log₂(64) = 6\n\n**Answer**: log₂(64) = 6"
              },
              {
                problem: "Simplify: log₇(7⁴)",
                solution: "**Step 1**: Recognize the inverse property\nlog_b(bˣ) = x\n\n**Step 2**: Apply directly\nlog₇(7⁴) = 4\n\nNo calculation needed — the log and exponential cancel!\n\n**Answer**: 4"
              }
            ]
          },
          practiceQuestions: [
            {
              question: "Evaluate: log₂(8)",
              answer: "3",
              solution: "2³ = 8, so log₂(8) = 3"
            },
            {
              question: "Evaluate: log₁₀(100)",
              answer: "2",
              solution: "10² = 100, so log₁₀(100) = 2"
            },
            {
              question: "Convert to log form: 3⁴ = 81",
              answer: "log₃(81) = 4",
              solution: "bˣ = y → log_b(y) = x"
            },
            {
              question: "Evaluate: log₅(1)",
              answer: "0",
              solution: "5⁰ = 1, so log₅(1) = 0"
            },
            {
              question: "Evaluate: log₄(4)",
              answer: "1",
              solution: "4¹ = 4, so log₄(4) = 1"
            },
            {
              question: "Convert to exponential form: log₂(32) = 5",
              answer: "2⁵ = 32",
              solution: "log_b(y) = x → bˣ = y"
            },
            {
              question: "Evaluate: log₃(27)",
              answer: "3",
              solution: "3³ = 27, so log₃(27) = 3"
            },
            {
              question: "Simplify: log₆(6⁷)",
              answer: "7",
              solution: "log_b(bˣ) = x, so log₆(6⁷) = 7"
            },
            {
              question: "Evaluate: log₁₀(1000)",
              answer: "3",
              solution: "10³ = 1000, so log₁₀(1000) = 3"
            },
            {
              question: "Convert to log form: 5² = 25",
              answer: "log₅(25) = 2",
              solution: "bˣ = y → log_b(y) = x"
            },
            {
              question: "Evaluate: log₂(1/4)",
              answer: "-2",
              solution: "2⁻² = 1/4, so log₂(1/4) = -2"
            },
            {
              question: "Simplify: 3^(log₃(10))",
              answer: "10",
              solution: "b^(log_b(x)) = x, so 3^(log₃(10)) = 10"
            },
            {
              question: "Evaluate: log₇(49)",
              answer: "2",
              solution: "7² = 49, so log₇(49) = 2"
            },
            {
              question: "Convert to exponential form: log₄(64) = 3",
              answer: "4³ = 64",
              solution: "log_b(y) = x → bˣ = y"
            },
            {
              question: "Evaluate: log₅(1/25)",
              answer: "-2",
              solution: "5⁻² = 1/25, so log₅(1/25) = -2"
            },
            {
              question: "Simplify: log₉(9)",
              answer: "1",
              solution: "log_b(b) = 1"
            },
            {
              question: "Evaluate: log₂(64)",
              answer: "6",
              solution: "2⁶ = 64, so log₂(64) = 6"
            },
            {
              question: "Convert to log form: 10⁻² = 0.01",
              answer: "log₁₀(0.01) = -2",
              solution: "bˣ = y → log_b(y) = x"
            },
            {
              question: "Evaluate: log₈(1)",
              answer: "0",
              solution: "8⁰ = 1, so log₈(1) = 0"
            },
            {
              question: "Simplify: log₄(4⁵)",
              answer: "5",
              solution: "log_b(bˣ) = x"
            }
          ]
        },
        {
          id: "logarithm-properties",
          title: "Logarithm Properties & Equations",
          duration: "50 min",
          difficulty: "Hard",
          content: {
            introduction: "The laws of logarithms allow us to simplify complex logarithmic expressions and solve logarithmic equations. These properties mirror the laws of exponents and are essential for advanced algebra.",
            
            textbookKnowledge: [
              {
                title: "Product Rule",
                content: "log_b(M · N) = log_b(M) + log_b(N)\n\nThe log of a product equals the sum of the logs.\n\nExample:\nlog₂(8 · 4) = log₂(8) + log₂(4)\nlog₂(32) = 3 + 2 = 5 ✓"
              },
              {
                title: "Quotient Rule",
                content: "log_b(M / N) = log_b(M) - log_b(N)\n\nThe log of a quotient equals the difference of the logs.\n\nExample:\nlog₃(27/9) = log₃(27) - log₃(9)\nlog₃(3) = 3 - 2 = 1 ✓"
              },
              {
                title: "Power Rule",
                content: "log_b(M^p) = p · log_b(M)\n\nThe log of a power equals the exponent times the log.\n\nExample:\nlog₂(8³) = 3 · log₂(8)\nlog₂(512) = 3 · 3 = 9 ✓"
              },
              {
                title: "Change of Base Formula",
                content: "log_b(M) = log_c(M) / log_c(b)\n\nMost commonly:\nlog_b(M) = log(M) / log(b)  or  ln(M) / ln(b)\n\nUse this to evaluate logs with any base on a calculator!"
              },
              {
                title: "Solving Logarithmic Equations",
                content: "**Method 1**: Combine logs using properties, then convert to exponential form\n\n**Method 2**: If log_b(M) = log_b(N), then M = N (one-to-one property)\n\n**Method 3**: Isolate the log, then exponentiate both sides"
              }
            ],
            
            shortMethods: [
              {
                title: "Mr. Augustine's Log Rules Rhyme",
                content: "**Product → Plus**\nlog(A · B) = log(A) + log(B)\n'Multiply inside, add outside'\n\n**Quotient → Minus**\nlog(A / B) = log(A) - log(B)\n'Divide inside, subtract outside'\n\n**Power → Times**\nlog(A^p) = p · log(A)\n'Power inside, times outside'\n\n**Memory Trick**: 'Inside operations become outside operations'"
              },
              {
                title: "The Expansion/Condensing Strategy",
                content: "**Expanding**: Break apart using rules\nlog₂(8x³) = log₂(8) + log₂(x³) = 3 + 3log₂(x)\n\n**Condensing**: Combine using rules\n2log(x) + log(y) = log(x²) + log(y) = log(x²y)\n\n**Tip**: Expand to simplify, condense to solve!"
              },
              {
                title: "Quick Equation Solver",
                content: "For log_b(x) = c:\n\n**Step 1**: Convert to exponential\nx = b^c\n\n**Step 2**: Calculate\n\nExample: log₂(x) = 5\nx = 2⁵ = 32\n\nDone in 2 steps!"
              }
            ],
            
            workedExamples: [
              {
                problem: "Expand: log₃(9x²/y)",
                solution: "**Step 1**: Apply quotient rule\nlog₃(9x²/y) = log₃(9x²) - log₃(y)\n\n**Step 2**: Apply product rule to numerator\nlog₃(9x²) = log₃(9) + log₃(x²)\n\n**Step 3**: Apply power rule\nlog₃(x²) = 2log₃(x)\n\n**Step 4**: Evaluate log₃(9)\nlog₃(9) = log₃(3²) = 2\n\n**Step 5**: Combine all parts\n= 2 + 2log₃(x) - log₃(y)\n\n**Answer**: 2 + 2log₃(x) - log₃(y)"
              },
              {
                problem: "Condense: 3log₂(x) - log₂(y) + log₂(4)",
                solution: "**Step 1**: Apply power rule (reverse)\n3log₂(x) = log₂(x³)\n\n**Step 2**: Evaluate log₂(4)\nlog₂(4) = log₂(2²) = 2\n\n**Step 3**: Rewrite\nlog₂(x³) - log₂(y) + 2\n\n**Step 4**: Convert 2 to log form\n2 = log₂(2²) = log₂(4)\n\n**Step 5**: Apply product rule\nlog₂(x³) + log₂(4) = log₂(4x³)\n\n**Step 6**: Apply quotient rule\nlog₂(4x³) - log₂(y) = log₂(4x³/y)\n\n**Answer**: log₂(4x³/y)"
              },
              {
                problem: "Solve: log₅(x + 3) = 2",
                solution: "**Step 1**: Convert to exponential form\nlog₅(x + 3) = 2  →  5² = x + 3\n\n**Step 2**: Evaluate the exponent\n25 = x + 3\n\n**Step 3**: Solve for x\nx = 25 - 3\nx = 22\n\n**Step 4**: Check (x must make argument positive)\nx + 3 = 22 + 3 = 25 > 0 ✓\n\n**Answer**: x = 22"
              }
            ]
          },
          practiceQuestions: [
            {
              question: "Expand: log₂(8x)",
              answer: "3 + log₂(x)",
              solution: "log₂(8x) = log₂(8) + log₂(x) = 3 + log₂(x)"
            },
            {
              question: "Condense: log(x) + log(y)",
              answer: "log(xy)",
              solution: "log(x) + log(y) = log(xy)"
            },
            {
              question: "Simplify: log₃(x⁴)",
              answer: "4log₃(x)",
              solution: "log₃(x⁴) = 4log₃(x)"
            },
            {
              question: "Solve: log₂(x) = 4",
              answer: "x = 16",
              solution: "x = 2⁴ = 16"
            },
            {
              question: "Expand: log₅(25/x)",
              answer: "2 - log₅(x)",
              solution: "log₅(25) - log₅(x) = 2 - log₅(x)"
            },
            {
              question: "Condense: 2log(x) - log(y)",
              answer: "log(x²/y)",
              solution: "log(x²) - log(y) = log(x²/y)"
            },
            {
              question: "Solve: log₃(x) = -2",
              answer: "x = 1/9",
              solution: "x = 3⁻² = 1/9"
            },
            {
              question: "Simplify: log₇(7³)",
              answer: "3",
              solution: "log₇(7³) = 3log₇(7) = 3·1 = 3"
            },
            {
              question: "Expand: log(x²y)",
              answer: "2log(x) + log(y)",
              solution: "log(x²) + log(y) = 2log(x) + log(y)"
            },
            {
              question: "Solve: log₄(x + 5) = 2",
              answer: "x = 11",
              solution: "x + 5 = 4² = 16\nx = 11"
            },
            {
              question: "Condense: log₂(x) + log₂(8)",
              answer: "log₂(8x)",
              solution: "log₂(x) + log₂(8) = log₂(8x)"
            },
            {
              question: "Expand: ln(e²x)",
              answer: "2 + ln(x)",
              solution: "ln(e²) + ln(x) = 2 + ln(x)"
            },
            {
              question: "Solve: log(x) = 3",
              answer: "x = 1000",
              solution: "x = 10³ = 1000"
            },
            {
              question: "Simplify: 3log₅(x)",
              answer: "log₅(x³)",
              solution: "3log₅(x) = log₅(x³)"
            },
            {
              question: "Expand: log₃(27x²)",
              answer: "3 + 2log₃(x)",
              solution: "log₃(27) + log₃(x²) = 3 + 2log₃(x)"
            },
            {
              question: "Solve: log₂(x - 1) = 3",
              answer: "x = 9",
              solution: "x - 1 = 2³ = 8\nx = 9"
            },
            {
              question: "Condense: log(x) - log(5)",
              answer: "log(x/5)",
              solution: "log(x) - log(5) = log(x/5)"
            },
            {
              question: "Expand: log₄(16/x³)",
              answer: "2 - 3log₄(x)",
              solution: "log₄(16) - log₄(x³) = 2 - 3log₄(x)"
            },
            {
              question: "Solve: ln(x) = 0",
              answer: "x = 1",
              solution: "x = e⁰ = 1"
            },
            {
              question: "Condense: 4log₃(x) + log₃(y)",
              answer: "log₃(x⁴y)",
              solution: "log₃(x⁴) + log₃(y) = log₃(x⁴y)"
            }
          ]
        }
      ]
    }
  ]
};